import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { hrApi as H, EMP_STATUS_STYLE, ATT_STATUS_STYLE, LEAVE_STATUS_STYLE, inr } from "@/lib/hr";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Users, CalendarCheck, Plane, Wallet, History } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = () => new Date().toISOString().slice(0, 7);

export default function HR() {
  const { can } = useAuth();
  const [meta, setMeta] = useState({ departments: [], shifts: [], att_statuses: [], leave_types: [], leave_statuses: [], emp_statuses: [] });
  const [stats, setStats] = useState({});
  const loadStats = () => H.stats().then(setStats).catch(() => {});
  useEffect(() => { H.meta().then(setMeta); loadStats(); }, []);

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">HR &amp; Attendance</h2>
        <p className="mt-1 text-sm text-slate-500">Employee master, daily attendance, leave approvals and monthly payroll.</p>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi testid="hr-stat-total" label="Employees" value={stats.total ?? 0} />
        <Kpi testid="hr-stat-active" label="Active" value={stats.active ?? 0} accent="text-emerald-600" />
        <Kpi testid="hr-stat-inactive" label="Inactive" value={stats.inactive ?? 0} accent="text-slate-500" />
        <Kpi testid="hr-stat-depts" label="Departments" value={stats.departments ?? 0} accent="text-indigo-600" />
        <Kpi testid="hr-stat-leaves" label="Pending leaves" value={stats.pending_leaves ?? 0} accent="text-amber-600" />
      </div>

      <Tabs defaultValue="employees">
        <TabsList data-testid="hr-tabs">
          <TabsTrigger value="employees" data-testid="tab-employees"><Users className="mr-1.5 h-4 w-4" />Employees</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><CalendarCheck className="mr-1.5 h-4 w-4" />Attendance</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave"><Plane className="mr-1.5 h-4 w-4" />Leave</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Wallet className="mr-1.5 h-4 w-4" />Payroll</TabsTrigger>
        </TabsList>
        <TabsContent value="employees" className="mt-4"><Employees meta={meta} can={can} onChanged={loadStats} /></TabsContent>
        <TabsContent value="attendance" className="mt-4"><Attendance meta={meta} can={can} /></TabsContent>
        <TabsContent value="leave" className="mt-4"><Leaves meta={meta} can={can} onChanged={loadStats} /></TabsContent>
        <TabsContent value="payroll" className="mt-4"><Payroll can={can} /></TabsContent>
      </Tabs>
    </div>
  );
}

const Kpi = ({ label, value, accent = "text-slate-900", testid }) => (
  <Card className="p-4" data-testid={testid}><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-1 font-heading text-xl font-bold ${accent}`}>{value}</p></Card>
);
const Th = ({ children }) => <TableHead className="text-xs uppercase text-slate-500">{children}</TableHead>;
const Spin = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

/* ------------------------------------------------------------------ Employees */
const EMPTY_EMP = { name: "", department: "", designation: "", mobile: "", email: "", address: "", date_of_joining: "", salary: "", allowances: "", deductions: "", shift: "General", reporting_manager: "" };

function Employees({ meta, can, onChanged }) {
  const canCreate = can("hr", "create");
  const canEdit = can("hr", "edit");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [status, setStatus] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    H.listEmployees({ department: dept === "all" ? undefined : dept, status: status || undefined, search: search || undefined })
      .then(setRows).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept, status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const toggle = async (r) => {
    try { await H.setEmpStatus(r.id, r.status === "Active" ? "Inactive" : "Active"); toast.success("Updated"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap gap-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code / designation" className="flex-1 min-w-[200px]" data-testid="employee-search" />
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-52" data-testid="employee-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All departments</SelectItem>{meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="employee-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
        </Select>
        {canCreate && <Button onClick={() => { setEditRow(null); setOpen(true); }} data-testid="add-employee-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />Add Employee</Button>}
      </div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Code</Th><Th>Name</Th><Th>Department</Th><Th>Designation</Th><Th>Shift</Th><Th>Salary</Th><Th>Status</Th><Th></Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`employee-row-${r.emp_code}`}>
                  <TableCell className="font-medium text-slate-800 cursor-pointer" onClick={() => setDetail(r.id)}>{r.emp_code}</TableCell>
                  <TableCell className="text-slate-700">{r.name}</TableCell>
                  <TableCell className="text-slate-600">{r.department || "—"}</TableCell>
                  <TableCell className="text-slate-600">{r.designation || "—"}</TableCell>
                  <TableCell className="text-slate-600">{r.shift}</TableCell>
                  <TableCell className="tabular-nums text-slate-700">{inr(r.salary)}</TableCell>
                  <TableCell><Badge className={`hover:bg-inherit ${EMP_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">{canEdit && <span className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setEditRow(r); setOpen(true); }} data-testid={`edit-employee-${r.emp_code}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`toggle-employee-${r.emp_code}`}>{r.status === "Active" ? "Deactivate" : "Activate"}</Button>
                  </span>}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-12 text-center text-slate-400" data-testid="employees-empty">No employees yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <EmployeeDialog open={open} onOpenChange={setOpen} meta={meta} row={editRow} onDone={() => { load(); onChanged?.(); }} />
      <EmployeeDetail eid={detail} onClose={() => setDetail(null)} />
    </Card>
  );
}

function EmployeeDialog({ open, onOpenChange, meta, row, onDone }) {
  const editing = !!row;
  const [f, setF] = useState(EMPTY_EMP);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(row ? { ...EMPTY_EMP, ...row } : EMPTY_EMP); }, [open, row]);
  const submit = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, salary: Number(f.salary) || 0, allowances: Number(f.allowances) || 0, deductions: Number(f.deductions) || 0,
        department: f.department || null, date_of_joining: f.date_of_joining || null };
      if (editing) await H.updateEmployee(row.id, payload); else await H.createEmployee(payload);
      toast.success(editing ? "Employee updated" : "Employee created"); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-600" />{editing ? `Edit ${row.emp_code}` : "Add Employee"}</DialogTitle>
          <DialogDescription>Salary &amp; pay heads feed monthly payroll.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="employee-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Department</Label>
            <Select value={f.department || ""} onValueChange={(v) => setF({ ...f, department: v })}><SelectTrigger data-testid="employee-dept-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Designation</Label><Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} data-testid="employee-designation-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Mobile</Label><Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="employee-mobile-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="employee-email-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Address</Label><Textarea rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Date of joining</Label><Input type="date" value={f.date_of_joining || ""} onChange={(e) => setF({ ...f, date_of_joining: e.target.value })} data-testid="employee-doj-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Shift</Label>
            <Select value={f.shift || "General"} onValueChange={(v) => setF({ ...f, shift: v })}><SelectTrigger data-testid="employee-shift-select"><SelectValue /></SelectTrigger><SelectContent>{meta.shifts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Monthly salary (₹)</Label><Input type="number" value={f.salary} onChange={(e) => setF({ ...f, salary: e.target.value })} data-testid="employee-salary-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Reporting manager</Label><Input value={f.reporting_manager} onChange={(e) => setF({ ...f, reporting_manager: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Allowances (₹/mo)</Label><Input type="number" value={f.allowances} onChange={(e) => setF({ ...f, allowances: e.target.value })} data-testid="employee-allowances-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Deductions (₹/mo)</Label><Input type="number" value={f.deductions} onChange={(e) => setF({ ...f, deductions: e.target.value })} data-testid="employee-deductions-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-employee-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save" : "Add")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeDetail({ eid, onClose }) {
  const [e, setE] = useState(null);
  useEffect(() => { if (eid) H.getEmployee(eid).then(setE); else setE(null); }, [eid]);
  if (!eid) return null;
  return (
    <Dialog open={!!eid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {!e ? (<><DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader><Spin /></>) : (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{e.emp_code} · {e.name}<Badge className={`hover:bg-inherit ${EMP_STATUS_STYLE[e.status]}`}>{e.status}</Badge></DialogTitle>
              <DialogDescription>{e.designation || "—"} · {e.department || "—"} · {e.shift}</DialogDescription></DialogHeader>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Stat l="Salary" v={inr(e.salary)} /><Stat l="Allowances" v={inr(e.allowances)} /><Stat l="Deductions" v={inr(e.deductions)} />
              <Stat l="Mobile" v={e.mobile || "—"} /><Stat l="Joined" v={e.date_of_joining || "—"} /><Stat l="Manager" v={e.reporting_manager || "—"} />
            </div>
            <Section title="Recent attendance">
              {(e.recent_attendance || []).slice(0, 8).map((a) => <Row key={a.id} testid={`emp-att-${a.id}`} left={a.date} mid={<Badge className={`hover:bg-inherit ${ATT_STATUS_STYLE[a.status]}`}>{a.status}</Badge>} right={a.overtime_hours ? `OT ${a.overtime_hours}h` : ""} />)}
              {(e.recent_attendance || []).length === 0 && <Empty>No attendance marked.</Empty>}
            </Section>
            <Section title="Leaves">
              {(e.leaves || []).map((l) => <Row key={l.id} testid={`emp-leave-${l.id}`} left={`${l.leave_type} · ${l.from_date} → ${l.to_date} (${l.days}d)`} mid={<Badge className={`hover:bg-inherit ${LEAVE_STATUS_STYLE[l.status]}`}>{l.status}</Badge>} />)}
              {(e.leaves || []).length === 0 && <Empty>No leaves.</Empty>}
            </Section>
            <Section title="Audit">
              {(e.audit || []).map((a) => <Row key={a.id} testid={`emp-audit-${a.id}`} left={`${a.action} — ${a.detail}`} right={<span className="text-xs text-slate-400">{a.by}</span>} />)}
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Attendance */
function Attendance({ meta, can }) {
  const canCreate = can("hr", "create");
  const [day, setDay] = useState(todayStr());
  const [emps, setEmps] = useState([]);
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [employees, existing] = await Promise.all([H.activeEmployees(), H.listAttendance({ date: day })]);
      setEmps(employees);
      const m = {};
      existing.forEach((a) => { m[a.employee_id] = { status: a.status, check_in: a.check_in || "", check_out: a.check_out || "", overtime_hours: a.overtime_hours || "" }; });
      employees.forEach((e) => { if (!m[e.id]) m[e.id] = { status: "Present", check_in: "", check_out: "", overtime_hours: "" }; });
      setMap(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [day]);
  const upd = (eid, k, v) => setMap((p) => ({ ...p, [eid]: { ...p[eid], [k]: v } }));
  const markAll = (status) => setMap((p) => { const n = { ...p }; emps.forEach((e) => { n[e.id] = { ...n[e.id], status }; }); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const entries = emps.map((e) => ({ employee_id: e.id, status: map[e.id]?.status || "Present",
        check_in: map[e.id]?.check_in || "", check_out: map[e.id]?.check_out || "", overtime_hours: Number(map[e.id]?.overtime_hours) || 0 }));
      await H.bulkAttendance({ date: day, entries });
      toast.success(`Attendance saved for ${day}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" data-testid="attendance-date" /></div>
        {canCreate && <>
          <Button size="sm" variant="outline" onClick={() => markAll("Present")} data-testid="mark-all-present-btn">Mark all present</Button>
          <Button size="sm" variant="outline" onClick={() => markAll("Week Off")}>Mark all week off</Button>
          <div className="flex-1" />
          <Button onClick={save} disabled={saving || emps.length === 0} data-testid="save-attendance-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save attendance"}</Button>
        </>}
      </div>
      {loading ? <Spin /> : emps.length === 0 ? <p className="py-12 text-center text-slate-400" data-testid="attendance-empty">No active employees. Add employees first.</p> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Code</Th><Th>Name</Th><Th>Status</Th><Th>Check in</Th><Th>Check out</Th><Th>OT (h)</Th></TableRow></TableHeader>
            <TableBody>
              {emps.map((e) => (
                <TableRow key={e.id} data-testid={`attendance-row-${e.emp_code}`}>
                  <TableCell className="font-medium text-slate-800">{e.emp_code}</TableCell>
                  <TableCell className="text-slate-700">{e.name}</TableCell>
                  <TableCell><Select value={map[e.id]?.status || "Present"} onValueChange={(v) => upd(e.id, "status", v)}>
                    <SelectTrigger className="w-36" data-testid={`att-status-${e.emp_code}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.att_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></TableCell>
                  <TableCell><Input type="time" value={map[e.id]?.check_in || ""} onChange={(ev) => upd(e.id, "check_in", ev.target.value)} className="w-32" /></TableCell>
                  <TableCell><Input type="time" value={map[e.id]?.check_out || ""} onChange={(ev) => upd(e.id, "check_out", ev.target.value)} className="w-32" /></TableCell>
                  <TableCell><Input type="number" value={map[e.id]?.overtime_hours || ""} onChange={(ev) => upd(e.id, "overtime_hours", ev.target.value)} className="w-20" data-testid={`att-ot-${e.emp_code}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ Leave */
function Leaves({ meta, can, onChanged }) {
  const canCreate = can("hr", "create");
  const canApprove = can("hr", "approve");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);

  const load = () => { setLoading(true); H.listLeaves({ status: status === "all" ? undefined : status }).then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const decide = async (id, decision) => {
    let note = null;
    if (decision === "reject") { note = window.prompt("Reason for rejection?"); if (!note) return; }
    try { await H.decideLeave(id, decision, note); toast.success(decision === "approve" ? "Approved" : "Rejected"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="leave-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{meta.leave_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex-1" />
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="apply-leave-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />Apply Leave</Button>}
      </div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Employee</Th><Th>Type</Th><Th>From</Th><Th>To</Th><Th>Days</Th><Th>Reason</Th><Th>Status</Th><Th></Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((l) => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  <TableCell className="text-slate-700">{l.emp_code} · {l.employee_name}</TableCell>
                  <TableCell className="text-slate-600">{l.leave_type}</TableCell>
                  <TableCell className="text-slate-600">{l.from_date}</TableCell>
                  <TableCell className="text-slate-600">{l.to_date}</TableCell>
                  <TableCell className="text-slate-600">{l.days}</TableCell>
                  <TableCell className="text-slate-500 max-w-[200px] truncate">{l.reason || "—"}</TableCell>
                  <TableCell><Badge className={`hover:bg-inherit ${LEAVE_STATUS_STYLE[l.status]}`}>{l.status}</Badge></TableCell>
                  <TableCell className="text-right">{canApprove && l.status === "Pending" && <span className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => decide(l.id, "approve")} data-testid={`approve-leave-${l.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="text-rose-600" onClick={() => decide(l.id, "reject")} data-testid={`reject-leave-${l.id}`}>Reject</Button>
                  </span>}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-12 text-center text-slate-400" data-testid="leaves-empty">No leave requests.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <LeaveDialog open={open} onOpenChange={setOpen} meta={meta} onDone={() => { load(); onChanged?.(); }} />
    </Card>
  );
}

function LeaveDialog({ open, onOpenChange, meta, onDone }) {
  const [emps, setEmps] = useState([]);
  const [f, setF] = useState({ employee_id: "", leave_type: "Casual", from_date: todayStr(), to_date: todayStr(), reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { H.activeEmployees().then(setEmps); setF({ employee_id: "", leave_type: "Casual", from_date: todayStr(), to_date: todayStr(), reason: "" }); } }, [open]);
  const submit = async () => {
    if (!f.employee_id) { toast.error("Select employee"); return; }
    setBusy(true);
    try { await H.applyLeave(f); toast.success("Leave applied"); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plane className="h-5 w-5 text-indigo-600" />Apply Leave</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Employee</Label>
            <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}><SelectTrigger data-testid="leave-employee-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.emp_code} · {e.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={f.leave_type} onValueChange={(v) => setF({ ...f, leave_type: v })}><SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger><SelectContent>{meta.leave_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div />
          <div className="space-y-1.5"><Label className="text-xs">From</Label><Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="leave-from-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">To</Label><Input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="leave-to-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Reason</Label><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-leave-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Payroll */
function Payroll({ can }) {
  const canApprove = can("hr", "approve");
  const [month, setMonth] = useState(monthStr());
  const [otRate, setOtRate] = useState("");
  const [data, setData] = useState({ run: null, payslips: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () => { setLoading(true); H.listPayslips(month).then(setData).catch(() => setData({ run: null, payslips: [] })).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  const run = async () => {
    setRunning(true);
    try { const res = await H.runPayroll(month, Number(otRate) || 0); setData(res); toast.success(`Payroll generated for ${month}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setRunning(false); }
  };
  const totalNet = useMemo(() => (data.payslips || []).reduce((s, p) => s + (p.net || 0), 0), [data]);

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="payroll-month" /></div>
        {canApprove && <>
          <div className="space-y-1"><Label className="text-xs">OT rate (₹/hour)</Label><Input type="number" value={otRate} onChange={(e) => setOtRate(e.target.value)} className="w-36" data-testid="payroll-ot-rate" /></div>
          <Button onClick={run} disabled={running} data-testid="run-payroll-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run / Regenerate payroll"}</Button>
        </>}
        <div className="flex-1" />
        {data.run && <div className="text-right"><p className="text-xs uppercase text-slate-500">Total net ({month})</p><p className="font-heading text-lg font-bold text-slate-900" data-testid="payroll-total-net">{inr(totalNet)}</p></div>}
      </div>
      {loading ? <Spin /> : (data.payslips || []).length === 0 ? <p className="py-12 text-center text-slate-400" data-testid="payroll-empty">No payroll for {month}. Run payroll to generate payslips.</p> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Code</Th><Th>Name</Th><Th>Days</Th><Th>OT h</Th><Th>Basic</Th><Th>Allow.</Th><Th>OT</Th><Th>Gross</Th><Th>Deduct.</Th><Th>Net</Th></TableRow></TableHeader>
            <TableBody>
              {data.payslips.map((p) => (
                <TableRow key={p.id} data-testid={`payslip-row-${p.emp_code}`}>
                  <TableCell className="font-medium text-slate-800">{p.emp_code}</TableCell>
                  <TableCell className="text-slate-700">{p.employee_name}</TableCell>
                  <TableCell className="text-slate-600">{p.payable_days}/{p.total_days}</TableCell>
                  <TableCell className="text-slate-600">{p.overtime_hours}</TableCell>
                  <TableCell className="tabular-nums text-slate-600">{inr(p.earned_basic)}</TableCell>
                  <TableCell className="tabular-nums text-slate-600">{inr(p.allowances)}</TableCell>
                  <TableCell className="tabular-nums text-slate-600">{inr(p.overtime_amount)}</TableCell>
                  <TableCell className="tabular-nums text-slate-700">{inr(p.gross)}</TableCell>
                  <TableCell className="tabular-nums text-rose-600">{inr(p.deductions)}</TableCell>
                  <TableCell className="tabular-nums font-semibold text-slate-900">{inr(p.net)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

const Stat = ({ l, v }) => <div className="rounded-md border border-slate-200 p-3"><p className="text-xs uppercase text-slate-500">{l}</p><p className="mt-1 font-heading text-base font-bold text-slate-900">{v}</p></div>;
const Section = ({ title, children }) => <div className="mt-3"><h4 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500"><History className="h-3.5 w-3.5" />{title}</h4><div className="space-y-1 max-h-52 overflow-y-auto">{children}</div></div>;
const Row = ({ left, mid, right, testid }) => <div className="flex items-center justify-between gap-2 rounded-md border border-slate-100 px-3 py-1.5 text-sm" data-testid={testid}><span className="flex-1 text-slate-700">{left}</span><span>{mid}</span><span>{right}</span></div>;
const Empty = ({ children }) => <p className="py-2 text-center text-xs text-slate-400">{children}</p>;
